import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
export function useNotifications(filter = {}) {
    const limit = filter.limit ?? 50;
    const page = filter.page ?? 1;
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String((page - 1) * limit));
    if (filter.unread !== undefined)
        params.set('unread', String(filter.unread));
    if (filter.range)
        params.set('range', filter.range);
    filter.severity?.forEach((s) => params.append('severity', s));
    return useQuery({
        queryKey: ['notifications', filter],
        queryFn: () => api(`/notifications?${params}`),
        // 60s safety-net only — the SSE channel in <ToastHost> calls
        // qc.invalidateQueries(['notifications']) on every push, so new rows
        // appear instantly while the panel is open. Polling here covers
        // SSE-down / proxy-timeout scenarios.
        refetchInterval: 60_000,
        refetchIntervalInBackground: false,
    });
}
export function useUnreadCount({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: () => api('/notifications/unread-count'),
        // `enabled` lets sections without the panel backend (e.g. Opengater in dev)
        // skip this poll so it doesn't 500-spam a backend that isn't there.
        enabled,
        // Same SSE-backed reasoning as `useNotifications`. The bell badge in
        // the sidebar refreshes via SSE-driven invalidation; this 60s tick is
        // just a fallback for when SSE is unreachable.
        refetchInterval: 60_000,
        refetchIntervalInBackground: false,
    });
}
export function useMarkRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/notifications/${id}/read`, { method: 'POST' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
}
export function useMarkAllRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api('/notifications/mark-all-read', { method: 'POST' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
}
export function useClearOne() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/notifications/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
}
export function useClearRead() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => api('/notifications/cleared', { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
}
// ─── Preferences ───────────────────────────────────────────────────────────
export const NOTIFICATION_CATEGORIES = [
    { id: 'backup_outcomes', label: 'Backup outcomes', desc: 'backup succeeded / partial / failed' },
    { id: 'restore', label: 'Restore', desc: 'restore initiated / completed / failed' },
    { id: 'schema_changes', label: 'Schema changes', desc: 'database / storage create / update / delete' },
    { id: 'health_checks', label: 'Health checks', desc: 'connection test results' },
    { id: 'retention_janitor', label: 'Retention & janitor', desc: 'retention sweep + watchdog cleanup' },
    { id: 'manual_actions', label: 'Manual actions', desc: 'manual backup / purge / download requests' },
    { id: 'critical', label: 'Critical events', desc: 'always-on override for errors and partial backups' },
];
export function useNotificationPrefs() {
    return useQuery({
        queryKey: ['notification-prefs'],
        queryFn: () => api('/notification-prefs'),
    });
}
export function useUpdateNotificationPrefs() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (prefs) => api('/notification-prefs', {
            method: 'PUT',
            body: { prefs },
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] }),
    });
}
